import React from 'react';

/**
 * Third Culture Leadership — Brand Asset Components
 *
 * RULE: Use the exact supplied brand assets. Do not recreate, redraw, or approximate.
 *
 * TCLBannerLogo  — Full horizontal logo (use on light/ivory backgrounds — the asset has a light bg)
 * TCLIconImage   — Standalone icon (use on light backgrounds or with a light container on dark bg)
 *
 * Asset URLs use the highest-quality uploads from the current session.
 */

const BANNER_URL = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/jxiucnvi_Third%20culture%20leadership%20banner.webp';
const ICON_URL   = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/c4h0snj7_Third%20culture%20Leadership%20icon.png';

/* Full horizontal logo — for nav (ivory bg) and footer logo-card */
export const TCLBannerLogo = ({ height = 44, className = '' }) => (
  <img
    src={BANNER_URL}
    alt="Third Culture Leadership"
    height={height}
    style={{ height: height + 'px', width: 'auto', display: 'block' }}
    className={className}
    draggable="false"
  />
);

/* Standalone icon — for compact placements (watermark, favicon, dark-bg icon-card) */
export const TCLIconImage = ({ size = 48, className = '' }) => (
  <img
    src={ICON_URL}
    alt="Third Culture Leadership"
    width={size}
    height={size}
    style={{ width: size + 'px', height: size + 'px', objectFit: 'contain', display: 'block' }}
    className={className}
    draggable="false"
  />
);

/* ── Legacy globe components kept for backward compatibility with admin pages ── */
const GOLD  = '#C99316';
const NAVY  = '#071B3A';
const IVORY = '#FAF8F3';
const SLATE = '#536A80';

/* ── Globe SVG icon (dark navy backgrounds) ──────────────────────────────── */
export const TCLIcon = ({ variant = 'gold-on-navy', size = 40, className = '' }) => {
  const isGoldOnNavy = variant === 'gold-on-navy';
  const ink = isGoldOnNavy ? GOLD : NAVY;
  const bg  = isGoldOnNavy ? NAVY : 'transparent';
  const sw  = 1.4;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Third Culture Leadership"
    >
      {bg !== 'transparent' && <rect width="44" height="44" rx="3" fill={bg} />}
      <circle cx="22" cy="22" r="17.5" stroke={ink} strokeWidth={sw} />
      <ellipse cx="22" cy="22" rx="17.5" ry="5" stroke={ink} strokeWidth={sw} />
      <ellipse cx="22" cy="13.5" rx="12" ry="3" stroke={ink} strokeWidth={sw} />
      <ellipse cx="22" cy="30.5" rx="12" ry="3" stroke={ink} strokeWidth={sw} />
      <line x1="22" y1="4.5" x2="22" y2="39.5" stroke={ink} strokeWidth={sw} />
      <path d="M 22 4.5 Q 6 22 22 39.5" stroke={ink} strokeWidth={sw} />
      <path d="M 22 4.5 Q 38 22 22 39.5" stroke={ink} strokeWidth={sw} />
    </svg>
  );
};

/* ── Wordmark lockup — SVG-based (dark backgrounds) ─────────────────────── */
export const TCLLockup = ({ onDark = true, height = 40, className = '' }) => {
  const textColor = onDark ? IVORY : NAVY;
  const globeSize = Math.round(height * 1.05);
  const line1 = Math.round(height * 0.285);
  const line2 = Math.round(height * 0.42);

  return (
    <div
      className={`flex items-center gap-3 ${className}`}
      role="img"
      aria-label="Third Culture Leadership"
      style={{ userSelect: 'none' }}
    >
      <TCLIcon
        variant={onDark ? 'gold-on-navy' : 'navy-on-light'}
        size={globeSize}
      />
      <div style={{ lineHeight: 1.1 }}>
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: `${line1}px`,
          fontWeight: 600,
          fontVariant: 'small-caps',
          letterSpacing: '0.14em',
          color: textColor,
        }}>
          Third Culture
        </div>
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: `${line2}px`,
          fontWeight: 400,
          fontStyle: 'italic',
          letterSpacing: '0.01em',
          color: GOLD,
          marginTop: '1px',
        }}>
          Leadership
        </div>
      </div>
    </div>
  );
};

export default TCLIcon;
